/** Stage E — Smart Reconsideration banner
 * Fetches pending reconsiderations and renders a compact stack with
 * Review / Snooze / Dismiss actions.
 */
import { useEffect, useState } from "react";
import { Sparkles, ChevronRight, Clock, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Reconsiderations({ onOpen }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/reconsiderations")
      .then(r => setItems(r.data?.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    // fire-and-forget scan so refresh brings in new triggers
    api.post("/reconsiderations/scan").then(load).catch(() => {});
  }, []);

  const act = async (rid, action) => {
    setBusyId(rid);
    try {
      await api.post(`/reconsiderations/${rid}/action`, { action, snooze_days: 7 });
      toast.success(action === "review" ? "Opening for review" :
                     action === "snooze" ? "Snoozed for 7 days" : "Dismissed");
      setItems(prev => prev.filter(i => i.id !== rid));
    } catch (e) { toast.error("Action failed"); }
    finally { setBusyId(null); }
  };

  if (loading || items.length === 0) return null;

  return (
    <div className="border border-signal-yellow/40 bg-signal-yellow/5 p-3 mb-4" data-testid="reconsiderations-banner">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={13} className="text-signal-yellow"/>
        <div className="label-cap text-signal-yellow">Smart Reconsideration</div>
        <span className="chip !text-[10px] border-current text-signal-yellow">{items.length}</span>
        <div className="ml-auto mono text-[10px] text-muted-foreground">Skipped opportunities that now meet your criteria</div>
      </div>
      <div className="space-y-2">
        {items.map(it => (
          <div key={it.id} className="border border-border/40 p-2 bg-card/40" data-testid={`reconsideration-${it.symbol}`}>
            <div className="flex items-baseline gap-2 mb-1">
              <div className="mono text-sm">{it.symbol}</div>
              {it.original_skip?.reason_code && (
                <span className="mono text-[10px] text-muted-foreground">skipped: {String(it.original_skip.reason_code).replace(/_/g, " ")}</span>
              )}
              <div className="ml-auto flex items-center gap-1">
                <button
                  onClick={() => { onOpen?.(it.symbol); act(it.id, "review"); }}
                  disabled={busyId === it.id}
                  className="chip pnl-up border-current text-[10px] hover:opacity-80"
                  data-testid={`recon-review-${it.symbol}`}
                >
                  <ChevronRight size={11} className="inline"/>Review
                </button>
                <button
                  onClick={() => act(it.id, "snooze")}
                  disabled={busyId === it.id}
                  className="btn-ghost h-6 !px-2 text-[10px]"
                  data-testid={`recon-snooze-${it.symbol}`}
                >
                  <Clock size={11}/> Snooze
                </button>
                <button
                  onClick={() => act(it.id, "dismiss")}
                  disabled={busyId === it.id}
                  className="btn-ghost h-6 !px-2 text-[10px]"
                  data-testid={`recon-dismiss-${it.symbol}`}
                  aria-label="Dismiss"
                >
                  <X size={11}/>
                </button>
              </div>
            </div>
            <ul className="text-xs text-foreground/85 space-y-0.5">
              {(it.triggers || []).map((t, idx) => (
                <li key={idx} className="pl-3 relative before:content-['·'] before:absolute before:left-0 before:top-0">
                  {t.message}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
